import { styled, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks';
import { Typography } from '../../atoms';
import { useCMSContent } from '../../../common/hooks/useCmsContent';
import { ReactComponent as GOEImage } from '../../../assets/icons/GOE_WORDMARK_pos3.svg';
import { checkFFEnabled } from '../../../utils';
import { FEATURE_NAMES } from '../../../common/constants';

const Styledfooter = styled('div')(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between'
}));

type FooterProps = {
    eulaVariant?: boolean;
    isPrimaryFooterDisplayed?: boolean;
    hideDisclaimerFooter?: boolean;
    primaryText?: string | React.ReactNode;
    secondaryText?: string | React.ReactNode;
    links?: { label: string; link: string }[];
};

export const Footer = ({
    // eulaVariant = false,
    primaryText = '',
    secondaryText,
    links = [],
    isPrimaryFooterDisplayed = true,
    hideDisclaimerFooter = true
}: FooterProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const cmsUtil = useCMSContent();
    const { globalConfig, currentLanguage } = useAppSelector((state) => state.global);
    const { isAuthenticated, user } = useAppSelector((state) => state.auth);
    const termsOfUse = globalConfig?.termsOfUse;

    const footerFFEnabled = checkFFEnabled(FEATURE_NAMES['FOOTER_REVAMP_FE_AEX-3479']) || !isAuthenticated || !user?.tenant;

    const filteredLinks = links?.filter((link) => !(!termsOfUse && link.label === t('UPDATED_TERMS_OF_USE_AGREEMENT')));

    // const isTrialSite = useAppSelector(
    //     (state) => state.global.globalConfig?.disclaimers?.showTrialFooter
    // );

    const onCookieSettingsClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (window.OneTrust) {
            console.log('language', currentLanguage);
            console.log('OneTrust object', window.OneTrust);
            window.OneTrust.ToggleInfoDisplay();
        }
    };

    const renderDisclaimerFooter = () =>
        !hideDisclaimerFooter && (
            <FooterWhiteContainer footerFFEnabled={footerFFEnabled}>
                <Styledfooter>
                    <Typography variant="body.caption" color="semantic.text.muted" data-testid="footer-description-special">
                        {cmsUtil.getContentAsElement('advisor_console.footer.part1')}
                    </Typography>
                </Styledfooter>
            </FooterWhiteContainer>
        );

    // Revamped footer layout (FOOTER_REVAMP_FE_AEX-3479 enabled)
    const renderRevampFooter = () => (
        <FooterBlueContainer theme={theme} isAuthenticated={isAuthenticated && user?.tenant} data-testid="footer-description-two">
            <div style={{ display: 'flex', gap: '32px' }}>
                <GOEImage />
                <VerticalLine />
            </div>

            <Typography variant="body.footerCaption" color={'semantic.text.default'}>
                {primaryText}
            </Typography>

            {secondaryText && (
                <Typography variant="body.footerCaption" color={'semantic.text.default'}>
                    {secondaryText}
                </Typography>
            )}

            <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
                <VerticalLine />
                <div style={{ display: 'flex', gap: '24px' }}>
                    {/* Disclaimer Link shown conditionally */}
                    {filteredLinks.map((el, index) => (
                        <a
                            href={el.link}
                            target="_blank"
                            rel="noreferrer"
                            key={`footer-link-${index}`}
                            aria-label={`${el.label}, ${t('OPENS_IN_NEW_WINDOW')}`}
                            style={{
                                textUnderlineOffset: '3px',
                                color: theme.palette.semantic.text.default
                            }}
                        >
                            <Typography variant="body.footerCaption" color={'semantic.text.default'}>
                                {el.label}
                            </Typography>
                        </a>
                    ))}
                    <button
                        type="button"
                        onClick={onCookieSettingsClick}
                        aria-haspopup="dialog"
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            textUnderlineOffset: '3px',
                            color: theme.palette.semantic.text.default,
                            textDecoration: 'underline',
                            cursor: 'pointer'
                        }}
                    >
                        <Typography variant="body.footerCaption" color={'semantic.text.default'}>
                            {t('COOKIE_SETTINGS')}
                        </Typography>
                    </button>
                </div>
            </div>
        </FooterBlueContainer>
    );

    // Legacy footer layout (FOOTER_REVAMP_FE_AEX-3479 disabled)
    const renderLegacyFooter = () => (
        <FooterBlueContainerLegacy theme={theme} isAuthenticated={isAuthenticated && user?.tenant} data-testid="footer-description-two">
            <Typography variant="body.footerCaption" color="semantic.text.inverse">
                {primaryText}
            </Typography>

            {secondaryText && (
                <Typography variant="body.footerCaption" color="semantic.text.inverse">
                    {secondaryText}
                </Typography>
            )}

            <div style={{ display: 'flex', gap: '24px' }}>
                {/* Disclaimer Link shown conditionally */}
                {filteredLinks.map((el, index) => (
                    <a
                        href={el.link}
                        target="_blank"
                        rel="noreferrer"
                        key={`footer-link-${index}`}
                        aria-label={`${el.label}, ${t('OPENS_IN_NEW_WINDOW')}`}
                        style={{
                            textUnderlineOffset: '3px',
                            color: theme.palette.semantic.text.inverse
                        }}
                    >
                        <Typography variant="body.footerCaption" color="semantic.text.inverse">
                            {el.label}
                        </Typography>
                    </a>
                ))}
                <button
                    type="button"
                    onClick={onCookieSettingsClick}
                    aria-haspopup="dialog"
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        textUnderlineOffset: '3px',
                        color: theme.palette.semantic.text.inverse,
                        textDecoration: 'underline',
                        cursor: 'pointer'
                    }}
                >
                    <Typography variant="body.footerCaption" color="semantic.text.inverse">
                        {t('COOKIE_SETTINGS')}
                    </Typography>
                </button>
            </div>
        </FooterBlueContainerLegacy>
    );

    return (
        <Container isAuthenticated={isAuthenticated} hideDisclaimerFooter={hideDisclaimerFooter} footerFFEnabled={footerFFEnabled}>
            {renderDisclaimerFooter()}
            {/* Only show if isPrimaryFooterDisplayed is true */}
            {isPrimaryFooterDisplayed && (footerFFEnabled ? renderRevampFooter() : renderLegacyFooter())}
        </Container>
    );
};

const FooterWhiteContainer = styled('div')<{ footerFFEnabled?: boolean }>(({ theme, footerFFEnabled }) => ({
    backgroundColor: 'layout.primary',
    padding: '16px 40px 12px 40px',
    background: theme.palette.neutral.white,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: footerFFEnabled ? '0px' : '28px'
}));

const FooterBlueContainer = styled('div')<{ isAuthenticated: boolean }>(({ theme }) => ({
    justifyContent: 'space-between',
    background: theme.palette.semantic.bg.subtle,
    display: 'flex',
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.semantic.bd.default}`,
    height: '48px',
    padding: '8px 40px'
}));

const FooterBlueContainerLegacy = styled('div')<{ isAuthenticated: boolean }>(({ theme, isAuthenticated = false }) => ({
    backgroundColor: 'layout.primary',
    padding: '8px 40px',
    justifyContent: 'space-between',
    background: isAuthenticated ? theme.palette.semantic.bg.brand : theme.palette.grey[400],
    display: 'flex',
    alignItems: 'center'
}));

const Container = styled('footer')<{ isAuthenticated: boolean; hideDisclaimerFooter: boolean; footerFFEnabled?: boolean }>(
    ({ hideDisclaimerFooter, footerFFEnabled }) => ({
        position: footerFFEnabled && !hideDisclaimerFooter ? 'sticky' : footerFFEnabled ? 'static' : 'sticky',
        display: 'flex',
        flexDirection: 'column',
        bottom: 0,
        width: '100%',
        zIndex: 100,
        flexShrink: 0 /* Prevent the header from shrinking */
    })
);

const VerticalLine = styled('div')(({ theme }) => ({
    width: '1px',
    height: '32px',
    borderRight: `1px solid ${theme.palette.semantic.bd.subtle}`
}));
